import { getPersistableEntities } from '@/lib/entityUtils';

export type JsonRecord = Record<string, unknown>;

export const ACTION_STATE_KEYS = [
  'suggestedActions',
  'queuedActions',
  'allSuggestedActions',
  'resolvedSuggestions',
  'discardedSuggestions',
  'discardedActions',
  'removedSuggestions',
  'partialSuggestions',
  'answeredPrompts',
  'partialPrompts',
  'actionGroups',
  'speechQueue',
] as const;

export function isJsonRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function hasActionStateProjection(value: unknown): boolean {
  if (!isJsonRecord(value)) {
    return false;
  }
  return ACTION_STATE_KEYS.some((key) => Array.isArray(value[key]));
}

export function hasCanonicalActionStateProjection(value: unknown): boolean {
  if (!isJsonRecord(value)) {
    return false;
  }

  const actionArrays = [
    value.suggestedActions,
    value.queuedActions,
    value.allSuggestedActions,
    value.resolvedSuggestions,
    value.discardedSuggestions,
    value.removedSuggestions,
    value.partialSuggestions,
  ].filter(Array.isArray) as unknown[][];

  return actionArrays.some((items) => items.some((item) => (
    isJsonRecord(item)
    && (
      typeof item.actionInstanceId === 'string'
      || typeof item.actionKey === 'string'
      || typeof item.canonicalRevision === 'number'
    )
  )));
}

export function stripActionState(value: JsonRecord): JsonRecord {
  const stripped = { ...value };
  for (const key of ACTION_STATE_KEYS) {
    delete stripped[key];
  }
  return stripped;
}

/** Checkpoints have one canonical state.toolSharing. Older report full_state
 * hydration can leave a reserved raw Redux duplicate under aiAnalysis; that
 * duplicate has no tool-selection authority and must never carry credentials.
 * Keep every other AI field and existing action-state projection policy.
 */
export function stripCheckpointAiAnalysis(value: JsonRecord): JsonRecord {
  const stripped = stripActionState(value);
  delete stripped.toolSharing;
  return stripped;
}

export function applyActionStateProjection(
  target: JsonRecord,
  source: unknown,
): JsonRecord {
  if (!isJsonRecord(source)) {
    return target;
  }

  const next = { ...target };
  for (const key of ACTION_STATE_KEYS) {
    if (Array.isArray(source[key])) {
      next[key] = source[key];
    }
  }
  return next;
}

export function mergeAfterCallAiAnalysisState(
  existingFullState: unknown,
  incomingFullState: JsonRecord,
  options: {
    preserveExistingActionState?: boolean;
    actionStateProjection?: unknown;
  } = {},
): JsonRecord {
  const existing = isJsonRecord(existingFullState) ? existingFullState : {};
  const incoming = options.preserveExistingActionState
    ? stripActionState(incomingFullState)
    : incomingFullState;
  const merged = {
    ...existing,
    ...incoming,
  };

  if (options.actionStateProjection) {
    return applyActionStateProjection(merged, options.actionStateProjection);
  }
  if (options.preserveExistingActionState) {
    return applyActionStateProjection(merged, existing);
  }
  return merged;
}

export function selectActionArchive(fullState: JsonRecord): unknown[] {
  if (Array.isArray(fullState.allSuggestedActions) && fullState.allSuggestedActions.length > 0) {
    return fullState.allSuggestedActions;
  }
  if (Array.isArray(fullState.suggestedActions)) {
    return fullState.suggestedActions;
  }
  if (Array.isArray(fullState.allSuggestedActions)) {
    return fullState.allSuggestedActions;
  }
  return [];
}

export function hasActivityFeedEvents(value: unknown): boolean {
  if (!isJsonRecord(value)) {
    return false;
  }
  return Array.isArray(value.events) && value.events.length > 0;
}

export function buildAfterCallAiAnalysisUpsert(
  callId: string,
  fullState: JsonRecord,
  updatedAt: string,
): Record<string, unknown> {
  const entitiesForPersistence = getPersistableEntities(fullState.entities);
  const actionArchive = selectActionArchive(fullState);
  const pinnedItems = Array.isArray(fullState.pinnedItems) ? fullState.pinnedItems : [];

  const payload: Record<string, unknown> = {
    call_id: callId,
    full_state: fullState,
    pinned_items: pinnedItems,
    suggested_actions: actionArchive,
    updated_at: updatedAt,
  };

  if (entitiesForPersistence) {
    payload.entities = entitiesForPersistence;
  }

  if (hasActivityFeedEvents(fullState.activityFeed)) {
    payload.activity_feed = fullState.activityFeed;
  }

  return payload;
}
